<template>
  <footer class="site-footer">
    <div class="site-footer__inner">

      <!-- Brand column -->
      <div class="site-footer__brand">
        <RouterLink to="/" class="site-footer__logo">
          <img src="/new-logo.png" class="site-footer__logo-mark" alt="">
          <span class="site-footer__logo-text">Maths Eleven Plus</span>
        </RouterLink>
        <p class="site-footer__slogan">Prepare with Precision.<br>Succeed with Confidence.</p>
        <p class="site-footer__tagline">
          Adaptive 11+ maths preparation for UK Grammar School success.
          4,800+ questions across 47 topic areas.
        </p>
        <div class="site-footer__trust">
          <span class="site-footer__trust-pill">🔒 Secure &amp; private</span>
          <span class="site-footer__trust-pill">🇬🇧 UK curriculum</span>
        </div>
      </div>

      <!-- Links columns -->
      <div class="site-footer__links-area">
        <div class="site-footer__col">
          <h4 class="site-footer__col-heading">Platform</h4>
          <a href="/#features" class="site-footer__link">Features</a>
          <a href="/#pricing" class="site-footer__link">Pricing</a>
          <RouterLink v-if="!isLoggedIn" to="/auth/register" class="site-footer__link">Get Started Free</RouterLink>
        </div>
        <div class="site-footer__col">
          <h4 class="site-footer__col-heading">Company</h4>
          <RouterLink to="/info/about" class="site-footer__link">About Us</RouterLink>
          <RouterLink to="/info/contact" class="site-footer__link">Contact Us</RouterLink>
          <RouterLink to="/info/faq" class="site-footer__link">FAQ</RouterLink>
        </div>
        <div class="site-footer__col">
          <h4 class="site-footer__col-heading">Legal</h4>
          <RouterLink to="/info/terms" class="site-footer__link">Terms &amp; Conditions</RouterLink>
          <RouterLink to="/info/privacy" class="site-footer__link">Privacy Policy</RouterLink>
          <RouterLink to="/info/cookies" class="site-footer__link">Cookie Policy</RouterLink>
        </div>
      </div>

    </div>

    <div class="site-footer__bottom">
      <span>© {{ year }} Polymorphic Tech Ltd. All rights reserved.</span>
      <span class="site-footer__bottom-sep">·</span>
      <RouterLink to="/info/terms" class="site-footer__bottom-link">Terms</RouterLink>
      <span class="site-footer__bottom-sep">·</span>
      <RouterLink to="/info/privacy" class="site-footer__bottom-link">Privacy</RouterLink>
      <span class="site-footer__bottom-sep">·</span>
      <RouterLink to="/info/cookies" class="site-footer__bottom-link">Cookies</RouterLink>
      <span class="site-footer__bottom-sep">·</span>
      <RouterLink to="/info/about" class="site-footer__bottom-link">About</RouterLink>
    </div>
  </footer>
</template>

<script setup>
import { computed } from 'vue';
import { useUserStore } from '@/stores/userStore';

const year = new Date().getFullYear();
const userStore = useUserStore();
const isLoggedIn = computed(() => !!userStore.session?.uid);
</script>

<style scoped>
.site-footer {
  background: #0D1628;
  color: rgba(255,255,255,0.75);
  font-family: 'Jost', sans-serif;
  text-align: left;
}

.site-footer__inner {
  max-width: 72rem;
  margin: 0;
  padding: 3rem 2rem 2rem;
  display: grid;
  grid-template-columns: 1fr;
  gap: 2.5rem;
}
@media (min-width: 768px) {
  .site-footer__inner {
    grid-template-columns: 1.4fr 1fr;
    gap: 3rem;
  }
}
@media (min-width: 1024px) {
  .site-footer__inner {
    grid-template-columns: 1.6fr 1fr;
  }
}

/* Brand */
.site-footer__logo {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  margin-bottom: 0.75rem;
}
.site-footer__logo-mark { width: 28px; height: 28px; flex-shrink: 0; }
.site-footer__logo-text {
  font-size: 1.1rem;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}
.site-footer__slogan {
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.15rem;
  font-style: italic;
  font-weight: 600;
  color: rgba(184,144,42,0.9);
  line-height: 1.4;
  margin-bottom: 0.5rem;
}
.site-footer__tagline {
  font-size: 0.875rem;
  line-height: 1.6;
  color: rgba(255,255,255,0.55);
  max-width: 320px;
  margin-bottom: 1.25rem;
}
.site-footer__trust {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.site-footer__trust-pill {
  background: rgba(255,255,255,0.07);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 9999px;
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  color: rgba(255,255,255,0.6);
}

/* Link columns */
.site-footer__links-area {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
}
@media (max-width: 480px) {
  .site-footer__links-area {
    grid-template-columns: repeat(2, 1fr);
  }
}

.site-footer__col-heading {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #B8902A;
  margin-bottom: 0.875rem;
}
.site-footer__col {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.site-footer__link {
  font-size: 0.875rem;
  color: rgba(255,255,255,0.6);
  text-decoration: none;
  transition: color 0.15s;
}
.site-footer__link:hover { color: #fff; }

/* Bottom bar */
.site-footer__bottom {
  border-top: 1px solid rgba(255,255,255,0.08);
  padding: 1rem 2rem;
  max-width: 72rem;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: rgba(255,255,255,0.35);
}
.site-footer__bottom-sep { color: rgba(255,255,255,0.2); }
.site-footer__bottom-link {
  color: rgba(255,255,255,0.4);
  text-decoration: none;
  transition: color 0.15s;
}
.site-footer__bottom-link:hover { color: rgba(255,255,255,0.7); }
</style>
